/* import __COLOCATED_TEMPLATE__ from './phone-reply-buttons.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type ConnectionPoint from 'embercom/models/operator/visual-builder/connection-point';
import type ChatMessage from 'embercom/models/operator/visual-builder/step/chat-message';
import type IntlService from 'ember-intl/services/intl';
import { type ReplyButtonsArgs } from './reply-buttons';
import type ReplyButtons from 'embercom/models/operator/visual-builder/step/reply-buttons';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import { type SyncHasMany } from '@ember-data/model';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';
import { type ConversationAttributeDescriptorListOption } from 'embercom/objects/inbox/conversation-attribute-descriptor';
import { FIN_SUPPORTED_LANGUAGES } from 'embercom/lib/ai-content-library/constants';

export interface PhoneReplyButtonsArgs extends ReplyButtonsArgs {
  step: ReplyButtons & ChatMessage;
  editorState: EditorState;
}

interface Signature {
  Element: HTMLDivElement;
  Args: PhoneReplyButtonsArgs;
}

const DEFAULT_LANGUAGE_LOCALES = ['en', 'fr', 'es'];
const QUICK_REPLY_MODE_LANGUAGE = 'language';
const MAX_VISIBLE_CDA_ITEMS = 6;

export default class PhoneReplyButtonsNodeItem extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  @tracked hasExistingBlocksOrHasBeenFocused = false;
  @tracked listAttributeDescriptors: ConversationAttributeDescriptor[] = [];

  constructor(owner: unknown, args: PhoneReplyButtonsArgs) {
    super(owner, args);

    this.hasExistingBlocksOrHasBeenFocused = !this.hasNoOrEmptyBlocks();
    this.onReplyButtonChange(this.args.step);
    this.loadListAttributeDescriptors();
  }

  get suffixMap() {
    let suffixMap = new Map<ConnectionPoint, string>();
    this.args.step.outwardConnectionPoints.map((button, index) => {
      let number = (index + 1) % 10;
      let suffixText = `${this.intl.t(
        'operator.workflows.visual-builder.reply-option-suffix',
      )} ${number}`;
      suffixMap.set(button, suffixText);
    });

    return suffixMap;
  }

  get isLinkedToCda() {
    return !!this.args.step.conversationAttributeDescriptorId;
  }

  get selectedDescriptor(): ConversationAttributeDescriptor | undefined {
    if (!this.args.step.conversationAttributeDescriptorId) {
      return undefined;
    }
    return this.listAttributeDescriptors.find(
      (d) => String(d.id) === String(this.args.step.conversationAttributeDescriptorId),
    );
  }

  get cdaDropdownOptions() {
    let cdaItems = this.listAttributeDescriptors.map((descriptor) => ({
      text: descriptor.name,
      icon: 'list' as const,
      value: String(descriptor.id),
      onSelectItem: () => this.onSelectConversationAttribute(String(descriptor.id)),
      component: 'workflows/graph-editor/popover-menu/popover-menu-item' as const,
      groupConfig: { iconBGClass: 'bg-neutral-container' },
    }));

    let languageItem = {
      text: this.intl.t('operator.workflows.visual-builder.phone-reply-buttons.language-mode'),
      icon: 'globe' as const,
      value: 'language-mode',
      onSelectItem: () => this.onSelectLanguageMode(),
      component: 'workflows/graph-editor/popover-menu/popover-menu-item' as const,
      groupConfig: { iconBGClass: 'bg-neutral-container' },
    };

    let visibleCdaItems = cdaItems.slice(0, MAX_VISIBLE_CDA_ITEMS);
    let overflowCdaItems = cdaItems.slice(MAX_VISIBLE_CDA_ITEMS);

    let groups = [{ items: [...visibleCdaItems, languageItem] }];
    if (overflowCdaItems.length > 0) {
      groups.push({ items: overflowCdaItems });
    }
    return groups;
  }

  get isLanguageMode() {
    return this.args.step.quickReplyMode === QUICK_REPLY_MODE_LANGUAGE;
  }

  get isReadOnlyButtons() {
    return this.isLinkedToCda || this.isLanguageMode;
  }

  get availableLanguages() {
    let currentLabels = new Set(
      this.args.step.outwardConnectionPoints.map((b) => b.label).filter(Boolean),
    );
    return FIN_SUPPORTED_LANGUAGES.filter((lang) => !currentLabels.has(lang.name));
  }

  get languageDropdownOptions() {
    let items = this.availableLanguages.map((lang) => ({
      text: lang.name,
      icon: 'list' as const,
      value: lang.name,
      onSelectItem: () => this.onAddLanguage(lang.name),
      component: 'workflows/graph-editor/popover-menu/popover-menu-item' as const,
      groupConfig: { iconBGClass: 'bg-neutral-container' },
    }));
    return [{ items }];
  }

  @action
  onSelectLanguageMode() {
    this.args.step.quickReplyMode = QUICK_REPLY_MODE_LANGUAGE;

    // Remove existing buttons and add default language buttons
    let existingButtons = this.args.step.outwardConnectionPoints.toArray();
    for (let button of existingButtons) {
      this.args.step.outwardConnectionPoints.removeObject(button);
    }

    let defaultLanguages = FIN_SUPPORTED_LANGUAGES.filter((lang) =>
      DEFAULT_LANGUAGE_LOCALES.includes(lang.locale),
    );
    for (let lang of defaultLanguages) {
      let button = this.store.createRecord('operator/visual-builder/connection-point', {
        label: lang.name,
      });
      this.args.step.outwardConnectionPoints.addObject(button);
    }

    this.hasExistingBlocksOrHasBeenFocused = false;
    this.maybeGenerateQuickReplyIntro();
    this.args.editorState.markAsEdited();
  }

  @action
  onAddLanguage(languageName: string) {
    let button = this.store.createRecord('operator/visual-builder/connection-point', {
      label: languageName,
    });
    this.args.step.outwardConnectionPoints.addObject(button);

    this.hasExistingBlocksOrHasBeenFocused = false;
    this.maybeGenerateQuickReplyIntro();
    this.args.editorState.markAsEdited();
  }

  @action
  onRemoveLanguageMode() {
    this.args.step.quickReplyMode = undefined;
    this.args.editorState.markAsEdited();
  }

  @action
  onFocusIntro() {
    this.hasExistingBlocksOrHasBeenFocused = true;
  }

  @action
  onReplyButtonChange(_step: ReplyButtons) {
    this.maybeGenerateQuickReplyIntro();
  }

  @action
  onReplyButtonReorder(_buttons: SyncHasMany<ConnectionPoint>) {
    this.maybeGenerateQuickReplyIntro();
  }

  @action
  onSelectConversationAttribute(descriptorId: string) {
    let descriptor = this.listAttributeDescriptors.find((d) => String(d.id) === descriptorId);
    if (!descriptor) {
      return;
    }

    this.args.step.conversationAttributeDescriptorId = descriptorId;

    let visibleOptions = (descriptor.listOptions || []).filter(
      (option: ConversationAttributeDescriptorListOption) => !option.archived,
    );

    let existingButtons = this.args.step.outwardConnectionPoints.toArray();
    let optionLabels = new Set(visibleOptions.map((o) => o.label));

    // Build a map of existing buttons by label for matching
    let existingByLabel = new Map<string, ConnectionPoint>();
    for (let button of existingButtons) {
      if (button.label) {
        existingByLabel.set(button.label, button);
      }
    }

    // Remove buttons that don't match any list option
    for (let button of existingButtons) {
      if (!button.label || !optionLabels.has(button.label)) {
        this.args.step.outwardConnectionPoints.removeObject(button);
      }
    }

    // Add new buttons for options that don't have a matching existing button
    for (let option of visibleOptions) {
      if (!existingByLabel.has(option.label)) {
        let button = this.store.createRecord('operator/visual-builder/connection-point', {
          label: option.label,
        });
        this.args.step.outwardConnectionPoints.addObject(button);
      }
    }

    this.hasExistingBlocksOrHasBeenFocused = false;
    this.maybeGenerateQuickReplyIntro();
    this.args.editorState.markAsEdited();
  }

  @action
  onRemoveConversationAttribute() {
    this.args.step.conversationAttributeDescriptorId = undefined;
    this.args.editorState.markAsEdited();
  }

  private async loadListAttributeDescriptors() {
    try {
      this.listAttributeDescriptors =
        await this.finVoiceConfigService.fetchConversationAttributeDescriptors();
    } catch {
      // Silently fail - CDA selector won't be populated but step still works
    }
  }

  private maybeGenerateQuickReplyIntro() {
    if (!this.hasExistingBlocksOrHasBeenFocused) {
      let blocksForQuickReplyIntro = this.generateTextForQuickReplies().map((text) =>
        this.store.createFragment('common/blocks/paragraph', {
          type: 'paragraph',
          text,
        }),
      );

      if (blocksForQuickReplyIntro.length > 0) {
        this.args.step.blocks = blocksForQuickReplyIntro as any;
        this.args.step.notifyPropertyChange('blocks');
        this.args.editorState.focusObject(this.args.step);
      }
    }
  }

  private hasNoOrEmptyBlocks() {
    return (
      this.args.step.blocks.length === 0 ||
      (this.args.step.blocks.length === 1 &&
        this.args.step.blocks.firstObject.type === 'paragraph' &&
        this.args.step.blocks.firstObject.text === '')
    );
  }

  private generateTextForQuickReplies() {
    return this.args.step.outwardConnectionPoints
      .filter((button) => button.type !== 'capture_composer_input')
      .filter((button) => !!button.label)
      .map((button, i) => {
        return `${button.label} press ${(i + 1) % 10}.`;
      });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::PhoneReplyButtons': typeof PhoneReplyButtonsNodeItem;
  }
}
